import React from 'react';

import {isElectron} from '../../../../libs/shared/core/src/helpers/environment/platformHelpers';
import {defineMessage, Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {MessageTypesEnum} from '../../../../libs/shared/core/src/models/api/commonMessageModel';
import {InboxModel} from '../../../../libs/shared/core/src/models/api/inboxModel';
import {DataEventActionsEnum} from '../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {DataEventContext} from '../../../../libs/shared/core/src/models/tracking/dataEventContexts';
import {OnboardingDataEventObject} from '../../../../libs/shared/core/src/models/tracking/objectNames/onboardingDataEventObject';
import {trackDataEvent} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {AppContentTypesEnum} from '../../../../libs/shared/core/src/store/panels/app/commonAppPanelState';
import {openInBrowser} from '../../../../srcCommon/browser/openInBrowser';
import {createSelector} from '../../../../srcCommon/helpers/reselectHelpers';
import {buildTeammateInboxLabelSectionExpandedKey} from '../../../helpers/expansionTogglingHelpers';
import {buildTeamWorkspaceExpandedKey} from '../../../helpers/models/localPreferencesHelpers';
import {InboxesFiltersEnum} from '../../../models/url/models/inboxesUrlModel';
import {ThunkAction} from '../../../store/appThunk';
import {makeGetComposeChannelExcludingDemo} from '../../../store/data/channels/channelsSelectors';
import {composeCommentAction} from '../../../store/data/comments/commentsActions';
import {
  getUserOwnedInboxes,
  getUserTeamParticipatingInboxes,
} from '../../../store/data/inboxes/inboxesSelectors';
import {setExpandItemAction} from '../../../store/data/localPreferences/localPreferencesActions';
import {composeMessageAction} from '../../../store/data/messages/draftMessagesActions';
import {saveCompletedOnboardingSetupGuideTaskAction} from '../../../store/data/preferences/preferencesActions';
import {
  getActivationPreferences,
  getCompletedStartGuideTasks,
  getShouldUseOpenFilterForTeams,
} from '../../../store/data/preferences/preferencesSelectors';
import {getFirstGlobalAdmin} from '../../../store/data/teammates/teammatesSelectors';
import {getFirstTeamUserIsPartOf, getIsInSandbox} from '../../../store/data/teams/teamsSelectors';
import {pushParametersAction} from '../../../store/panels/app/appPanelRouteActions';
import {InboxesParametersTypesEnum} from '../../../store/panels/inboxes/parameters/inboxesParameters';
import {emptyInboxesSelection} from '../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {getInboxesParametersBuilder} from '../../../store/panels/inboxes/parameters/inboxesParametersSelectors';
import {getLocalize} from '../../../store/stateless/internationalizationSelectors';
import {OnboardingGuidesEnum} from '../guide/onboardingGuideTypes';
import {FluSetupGuideTaskDefinition, FluSetupGuideTaskNameEnum} from './onboardingTypes';

/*
 * Constants.
 */

const fluSetupGuideContext: DataEventContext = Object.freeze({
  name: 'flu_setup_guide',
  payload: {},
});

// Fallback values for teammates who completed the old "Start guide."
enum legacyStartGuideTaskTypesEnum {
  SEND_FIRST_EMAIL = 'send_email',
  DOWNLOAD_APP = 'download_app',
}

/*
 * Selectors.
 */

const getFirstSharedInbox = createSelector(
  [getUserTeamParticipatingInboxes, getIsInSandbox],
  (teamParticipatingInboxes, isInSandbox): InboxModel | undefined =>
    teamParticipatingInboxes.filter((inbox) => !isInSandbox(inbox))[0],
);

const getFirstIndividualInbox = createSelector(
  [getUserOwnedInboxes, getIsInSandbox],
  (inboxes, isInSandbox): InboxModel | undefined => inboxes.filter((inbox) => !isInSandbox(inbox))[0],
);

const getIsSendFirstEmailTaskComplete = createSelector(
  [getActivationPreferences, getCompletedStartGuideTasks],
  (activationPreferences, legacyStartGuideTasks: ReadonlyArray<string>): boolean =>
    activationPreferences.setupGuideTasks?.includes(FluSetupGuideTaskNameEnum.SEND_FIRST_EMAIL) ||
    legacyStartGuideTasks.includes(legacyStartGuideTaskTypesEnum.SEND_FIRST_EMAIL),
);

const getIsDownloadAppTaskComplete = createSelector(
  [getActivationPreferences, getCompletedStartGuideTasks],
  (activationPreferences, legacyStartGuideTasks: ReadonlyArray<string>): boolean =>
    isElectron() ||
    activationPreferences.setupGuideTasks?.includes(FluSetupGuideTaskNameEnum.DOWNLOAD_APP) ||
    legacyStartGuideTasks.includes(legacyStartGuideTaskTypesEnum.DOWNLOAD_APP),
);

/*
 * Actions.
 */

function composeAccessSharedInboxDiscussion(): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();
    const localize = getLocalize(state);

    const firstGlobalAdmin = getFirstGlobalAdmin(state);

    if (!firstGlobalAdmin) {
      return; // This shouldn't happen...
    }

    dispatch(
      composeCommentAction({
        text: localize(
          defineMessage({
            id: 'flu-onboarding-task-access-shared-inbox-comment',
            message: 'Please grant me access to our shared inboxes. Thank you!',
          }),
        ),
        topic: localize(
          defineMessage({
            id: 'flu-onboarding-task-access-shared-inbox-topic',
            message: 'Shared inbox access',
          }),
        ),
        teammateIds: [firstGlobalAdmin.id],
      }),
    );
  };
}

/*
 * Access a shared inbox.
 */

const accessSharedInbox = Object.freeze<FluSetupGuideTaskDefinition>({
  key: 'accessSharedInbox',
  getIsTaskVisible: () => true,
  getIsTaskComplete: (state) => !!getFirstSharedInbox(state),
  computeTrackingObject: () => 'access_shared_inbox',
  isUnnumbered: false,
  title: <Trans id="flu-onboarding-task-access-shared-inbox-title">Access your shared inbox</Trans>,
  description: (
    <Trans id="flu-onboarding-task-access-shared-inbox-description">
      Access your shared inbox and resolve customer inquiries with your team.
    </Trans>
  ),
  startTask: ({dispatch, state, panelId}) => {
    trackDataEvent(
      fluSetupGuideContext,
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.FRONT_LINE_USER_SETUP_GUIDE_GET_STARTED_ITEM,
      {
        item: 'access_shared_inbox',
      },
    );

    const teamInbox = getFirstSharedInbox(state);
    const team = getFirstTeamUserIsPartOf(state);

    if (!(teamInbox && team)) {
      dispatch(composeAccessSharedInboxDiscussion());
      return;
    }

    const buildInboxesParameters = getInboxesParametersBuilder(state);
    const shouldUseOpenFilterForTeams = getShouldUseOpenFilterForTeams(state);

    const parameters = buildInboxesParameters({
      type: InboxesParametersTypesEnum.INBOX,
      inboxId: teamInbox.id,
      filters: [shouldUseOpenFilterForTeams ? InboxesFiltersEnum.OPEN : InboxesFiltersEnum.UNASSIGNED],
      selection: emptyInboxesSelection,
    });

    dispatch(setExpandItemAction(buildTeamWorkspaceExpandedKey(team.namespace), true));

    if (!panelId) {
      return;
    }

    dispatch(
      pushParametersAction(panelId, {
        contentType: AppContentTypesEnum.INBOXES,
        parameters,
      }),
    );
  },
});

/*
 * Add your work email.
 */

const addIndividualInbox = Object.freeze<FluSetupGuideTaskDefinition>({
  key: 'addIndividualInbox',
  getIsTaskVisible: () => true,
  getIsTaskComplete: (state) => !!getFirstIndividualInbox(state),
  computeTrackingObject: () => 'add_individual_inbox',
  isUnnumbered: false,
  title: <Trans id="flu-onboarding-task-add-individual-inbox-title">Add your work email</Trans>,
  description: (
    <Trans id="flu-onboarding-task-add-individual-inbox-description">
      Manage personal and shared inboxes inside of Front and eliminate the need to switch between systems.
    </Trans>
  ),
  startTask: ({dispatch, state, panelId, setOnboardingGuide}) => {
    trackDataEvent(
      fluSetupGuideContext,
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.FRONT_LINE_USER_SETUP_GUIDE_GET_STARTED_ITEM,
      {
        item: 'add_individual_inbox',
      },
    );

    const individualInbox = getFirstIndividualInbox(state);

    if (!individualInbox) {
      setOnboardingGuide(OnboardingGuidesEnum.FRONT_LINE_USER_ADD_EMAIL_POPOVER);
      return;
    }

    const buildInboxesParameters = getInboxesParametersBuilder(state);
    const shouldUseOpenFilterForTeams = getShouldUseOpenFilterForTeams(state);

    const parameters = buildInboxesParameters({
      type: InboxesParametersTypesEnum.TEAMMATE_INBOX,
      inboxId: individualInbox.id,
      filters: [shouldUseOpenFilterForTeams ? InboxesFiltersEnum.OPEN : InboxesFiltersEnum.UNASSIGNED],
      selection: emptyInboxesSelection,
    });

    dispatch(setExpandItemAction(buildTeammateInboxLabelSectionExpandedKey(individualInbox.namespace), true));

    if (!panelId) {
      return;
    }

    dispatch(
      pushParametersAction(panelId, {
        contentType: AppContentTypesEnum.INBOXES,
        parameters,
      }),
    );
  },
});

/*
 * Send your first email.
 */

const sendFirstEmail = Object.freeze<FluSetupGuideTaskDefinition>({
  key: 'sendFirstEmail',
  getIsTaskVisible: () => true,
  getIsTaskComplete: (state) => getIsSendFirstEmailTaskComplete(state),
  computeTrackingObject: () => 'send_first_email',
  isUnnumbered: false,
  title: <Trans id="flu-onboarding-task-send-first-email-title">Send your first email</Trans>,
  description: (
    <Trans id="flu-onboarding-task-send-first-email-description">
      Start replying to customers today in Front.
    </Trans>
  ),
  startTask: ({dispatch, state, setOnboardingGuide}) => {
    trackDataEvent(
      fluSetupGuideContext,
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.FRONT_LINE_USER_SETUP_GUIDE_GET_STARTED_ITEM,
      {
        item: 'send_first_email',
      },
    );

    const individualInbox = getFirstIndividualInbox(state);

    const individualChannel = makeGetComposeChannelExcludingDemo({
      getSpecifiedMessageType: () => MessageTypesEnum.EMAIL,
    })(state, {});

    if (!(individualInbox && individualChannel)) {
      setOnboardingGuide(OnboardingGuidesEnum.FRONT_LINE_USER_SEND_EMAIL_POPOVER);
      return;
    }

    dispatch(saveCompletedOnboardingSetupGuideTaskAction(FluSetupGuideTaskNameEnum.SEND_FIRST_EMAIL));
    dispatch(composeMessageAction({channelId: individualChannel.id}));
  },
});

/*
 * Download desktop app.
 */

const downloadApp = Object.freeze<FluSetupGuideTaskDefinition>({
  key: 'downloadApp',
  getIsTaskVisible: () => !isElectron(),
  getIsTaskComplete: (state) => getIsDownloadAppTaskComplete(state),
  computeTrackingObject: () => 'download_app',
  isExternal: true,
  isUnnumbered: false,
  title: <Trans id="flu-onboarding-task-download-app-title">Download desktop app</Trans>,
  description: (
    <Trans id="flu-onboarding-task-download-app-description">
      Stay connected across devices with Front’s desktop and mobile apps.
    </Trans>
  ),
  startTask: ({dispatch}) => {
    trackDataEvent(
      fluSetupGuideContext,
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.FRONT_LINE_USER_SETUP_GUIDE_GET_STARTED_ITEM,
      {
        item: 'download_app',
      },
    );

    dispatch(saveCompletedOnboardingSetupGuideTaskAction(FluSetupGuideTaskNameEnum.DOWNLOAD_APP));
    openInBrowser('https://front.com/download');
  },
});

/*
 * Setup Guide Task Definitions.
 */

export const fluOnboardingTaskDefinitions = {
  start: [accessSharedInbox, addIndividualInbox, sendFirstEmail, downloadApp],
};
